import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckSquare, Filter, LayoutGrid, List, Plus, Search, Share2 } from "lucide-react";
import { toast } from "sonner";
import { useItemOptions } from "@/lib/item-options";
import {
  CONDITIONS, ItemFilters, ItemListResponse,
  SEASONS, clientsApi, fileUrl, formatMoney, itemsApi, locationsApi, taxonomyApi, sharesApi,
  ClientRecord, LocationRecord, FacetBucket, SharedLink,
} from "@/lib/catalog-api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui-shim";
import { ItemCard } from "@/components/catalog/ItemCard";
import { ItemGridSkeleton } from "@/components/catalog/Skeleton";
import { BulkActionsBar } from "@/components/catalog/BulkActionsBar";
import { HelpOverlay } from "@/components/catalog/HelpOverlay";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui-shim";
import { useKeyboardShortcuts } from "@/lib/keyboard-shortcuts";

type ViewMode = "grid" | "list";

const SORTS = [
  { value: "created_desc", label: "Newest first" },
  { value: "created_asc", label: "Oldest first" },
  { value: "name_asc", label: "Title (A→Z)" },
  { value: "name_desc", label: "Title (Z→A)" },
  { value: "brand_asc", label: "Brand (A→Z)" },
  { value: "brand_desc", label: "Brand (Z→A)" },
  { value: "value_desc", label: "Value (high→low)" },
  { value: "value_asc", label: "Value (low→high)" },
];

const PAGE_SIZES = ["24", "48", "96", "200"];
const DEFAULT_PAGE_SIZE = 48;

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
}

export default function Items() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [data, setData] = useState<ItemListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(params.get("q") || "");
  const debouncedSearch = useDebounced(search, 350);
  const [clients, setClients] = useState<ClientRecord[]>([]);
  const [locations, setLocations] = useState<LocationRecord[]>([]);
  const [locationTypes, setLocationTypes] = useState<string[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [brands, setBrands] = useState<string[]>([]);
  const [grouped, setGrouped] = useState<import("@/lib/catalog-api").GroupedGroup[] | null>(null);
  const groupBy = params.get("group") || "none";
  const [portalOpen, setPortalOpen] = useState(false);
  const [selecting, setSelecting] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [view, setView] = useState<ViewMode>(() =>
    (localStorage.getItem("wi-items-view") as ViewMode) || "grid"
  );
  const searchRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    localStorage.setItem("wi-items-view", view);
  }, [view]);

  const filters: ItemFilters = useMemo(() => ({
    q: debouncedSearch || undefined,
    brand: params.getAll("brand"),
    category: params.getAll("category"),
    condition: params.getAll("condition"),
    season: params.getAll("season"),
    client_id: params.get("client_id") || undefined,
    location_id: params.get("location_id") || undefined,
    location_type: params.getAll("location_type"),
    size: params.getAll("size"),
    color_family: params.getAll("color_family"),
    material: params.getAll("material"),
    shoe_type: params.getAll("shoe_type"),
    sort: (params.get("sort") as any) || "created_desc",
    archived: params.get("archived") === "1",
    page: Number(params.get("page") || 1),
    page_size: Number(params.get("page_size") || DEFAULT_PAGE_SIZE),
  }), [params, debouncedSearch]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      if (groupBy !== "none") {
        const g = await itemsApi.grouped(filters, groupBy);
        setGrouped(g.groups);
        setData(await itemsApi.list({ ...filters, page: 1, page_size: 1 }));
      } else {
        setGrouped(null);
        setData(await itemsApi.list(filters));
      }
    } finally { setLoading(false); }
  }, [filters, groupBy]);

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => {
    clientsApi.list().then(setClients).catch(() => {});
    locationsApi.list().then(setLocations).catch(() => {});
    taxonomyApi.locationTypes().then((r) => setLocationTypes(r.location_types)).catch(() => {});
    taxonomyApi.categories().then((r) => setCategories(r.categories)).catch(() => {});
    taxonomyApi.brands().then((r) => setBrands(r.brands)).catch(() => {});
  }, []);

  // Keyboard shortcuts — `g` toggles grid/list view (NOT select mode),
  // `?` opens the help overlay.
  useKeyboardShortcuts({
    onSearch: () => searchRef.current?.focus(),
    onNew: () => navigate("/app/items/new"),
    onToggleView: () => setView((v) => (v === "grid" ? "list" : "grid")),
    onFilter: () => setMobileFilterOpen(true),
    onHelp: () => setHelpOpen(true),
    onEscape: () => {
      if (helpOpen) setHelpOpen(false);
      else if (selected.size > 0) { setSelected(new Set()); setSelecting(false); }
      else if (mobileFilterOpen) setMobileFilterOpen(false);
      else if (document.activeElement === searchRef.current) {
        searchRef.current?.blur();
      }
    },
  });

  function toggleMulti(key: "brand" | "category" | "condition" | "season", value: string) {
    const sp = new URLSearchParams(params);
    const cur = sp.getAll(key);
    sp.delete(key);
    if (cur.includes(value)) cur.filter((c) => c !== value).forEach((c) => sp.append(key, c));
    else [...cur, value].forEach((c) => sp.append(key, c));
    sp.delete("page");
    setParams(sp);
  }
  function setSingle(key: string, value: string | null) {
    const sp = new URLSearchParams(params);
    if (value) sp.set(key, value); else sp.delete(key);
    sp.delete("page");
    setParams(sp);
  }
  // Page changes must NOT reset the page param (setSingle deliberately does for
  // filters/sort). This dedicated setter is what fixed pagination being stuck.
  function setPage(p: number) {
    const sp = new URLSearchParams(params);
    sp.set("page", String(p));
    setParams(sp);
  }
  function setPageSize(size: string) {
    const sp = new URLSearchParams(params);
    sp.set("page_size", size);
    sp.delete("page");
    setParams(sp);
  }
  function reset() { setSearch(""); setParams(new URLSearchParams()); }

  function toggleSelect(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  function exitSelectMode() { setSelected(new Set()); setSelecting(false); }
  async function selectAll() {
    try {
      const res = await itemsApi.allIds(filters);
      if (res.total === 0) { toast.info("No items to select."); return; }
      setSelecting(true);
      setSelected(new Set(res.ids));
    } catch { toast.error("Could not select all items."); }
  }

  const activeFilterCount =
    params.getAll("brand").length + params.getAll("category").length +
    params.getAll("condition").length + params.getAll("season").length +
    params.getAll("location_type").length + params.getAll("size").length +
    params.getAll("color_family").length + params.getAll("material").length +
    params.getAll("shoe_type").length +
    (params.get("client_id") ? 1 : 0) + (params.get("location_id") ? 1 : 0);

  const filterPanel = (
    <FilterPanel
      data={data} params={params} clients={clients} locations={locations}
      locationTypes={locationTypes} categories={categories} brands={brands}
      onToggleMulti={toggleMulti} onSetSingle={setSingle} onReset={reset}
      activeFilterCount={activeFilterCount}
    />
  );

  return (
    <main className="flex-1 px-6 md:px-10 py-10" data-testid="items-page">
      <div className="flex items-baseline justify-between flex-wrap gap-4 mb-8">
        <div>
          <p className="text-[11px] uppercase tracking-[0.28em] text-oxblood font-medium mb-2">— Catalog</p>
          <h1 className="font-display text-3xl sm:text-4xl">Items</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <div className="flex border border-border" data-testid="items-view-toggle" role="group" aria-label="Layout">
            <button type="button" onClick={() => setView("grid")}
              data-testid="items-view-grid"
              aria-pressed={view === "grid"}
              aria-label="Grid view"
              className={`h-10 w-10 grid place-items-center transition-colors ${
                view === "grid" ? "bg-foreground text-ivory" : "text-muted-foreground hover:text-foreground"
              }`}>
              <LayoutGrid className="w-4 h-4" />
            </button>
            <button type="button" onClick={() => setView("list")}
              data-testid="items-view-list"
              aria-pressed={view === "list"}
              aria-label="List view"
              className={`h-10 w-10 grid place-items-center transition-colors ${
                view === "list" ? "bg-foreground text-ivory" : "text-muted-foreground hover:text-foreground"
              }`}>
              <List className="w-4 h-4" />
            </button>
          </div>
          <Button variant="outline"
            data-testid="items-select-mode"
            onClick={() => { if (selecting) exitSelectMode(); else setSelecting(true); }}
            className={`rounded-none border-foreground/20 hover:bg-bone ${selecting ? "bg-oxblood/10 text-oxblood-deep" : ""}`}>
            <CheckSquare className="w-4 h-4 mr-2" />
            {selecting ? "Done" : "Select"}
          </Button>
          {selecting && (
            <Button variant="outline" data-testid="items-select-all" onClick={selectAll}
              className="rounded-none border-foreground/20 hover:bg-bone">
              Select all {data?.total ?? 0}
            </Button>
          )}
          <Button variant="outline" data-testid="items-portal-btn"
            onClick={() => setPortalOpen(true)}
            className="rounded-none border-foreground/20 hover:bg-bone h-10">
            <Share2 className="w-4 h-4 mr-2" />Client portal
          </Button>
          <Button asChild data-testid="items-new-btn"
            className="bg-oxblood hover:bg-oxblood-deep text-ivory rounded-none h-10 px-5">
            <Link to="/app/items/new"><Plus className="w-4 h-4 mr-2" />Catalog new item</Link>
          </Button>
        </div>
      </div>
      <ClientPortalDialog open={portalOpen} onClose={() => setPortalOpen(false)} />

      {/* Mobile filter trigger */}
      <div className="lg:hidden mb-4 flex items-center justify-between gap-2">
        <Button variant="outline" onClick={() => setMobileFilterOpen(true)}
          data-testid="items-mobile-filter-btn"
          className="rounded-none border-foreground/20 hover:bg-bone shrink-0">
          <Filter className="w-4 h-4 mr-2" />
          Filter{activeFilterCount > 0 && ` · ${activeFilterCount}`}
        </Button>
        <div className="flex items-center gap-2">
          <select data-testid="items-sort-mobile" className="h-9 border border-border bg-background px-2 text-sm"
            value={params.get("sort") || "created_desc"}
            onChange={(e) => setSingle("sort", e.target.value)}>
            {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <select data-testid="items-page-size-mobile" className="h-9 border border-border bg-background px-2 text-sm"
            value={String(data?.page_size || DEFAULT_PAGE_SIZE)}
            onChange={(e) => setPageSize(e.target.value)}>
            {PAGE_SIZES.map((s) => <option key={s} value={s}>{s}/pg</option>)}
          </select>
        </div>
      </div>

      <div className="grid lg:grid-cols-[260px_1fr] gap-8">
        <aside data-testid="items-filters" className="hidden lg:block">
          <div className="space-y-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input ref={searchRef} data-testid="items-search" placeholder="Search name, brand, tags…  ( / )"
                value={search} onChange={(e: any) => setSearch(e.target.value)} className="pl-9" />
            </div>
            {filterPanel}
          </div>
        </aside>

        <section>
          <div className="hidden lg:flex items-center justify-between mb-5">
            <p data-testid="items-count" className="text-sm text-muted-foreground">
              {data ? `${data.total} item${data.total === 1 ? "" : "s"}` : "Loading…"}
            </p>
            <div className="flex items-center gap-2">
              <label className="text-xs text-muted-foreground">Sort</label>
              <select data-testid="items-sort" className="h-9 border border-border bg-background px-2 text-sm"
                value={params.get("sort") || "created_desc"}
                onChange={(e) => setSingle("sort", e.target.value)}>
                {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
              <label className="text-xs text-muted-foreground ml-2">Group</label>
              <select data-testid="items-group-by" className="h-9 border border-border bg-background px-2 text-sm"
                value={params.get("group") || "none"}
                onChange={(e) => setSingle("group", e.target.value === "none" ? null : e.target.value)}>
                <option value="none">No grouping</option>
                <option value="location">By location</option>
                <option value="location_type">By location type</option>
                <option value="location_then_type">By location → type</option>
              </select>
              <label className="text-xs text-muted-foreground ml-2">Show</label>
              <select data-testid="items-page-size" className="h-9 border border-border bg-background px-2 text-sm"
                value={String(data?.page_size || DEFAULT_PAGE_SIZE)}
                onChange={(e) => setPageSize(e.target.value)}>
                {PAGE_SIZES.map((s) => <option key={s} value={s}>{s} / page</option>)}
              </select>
            </div>
          </div>

          {loading ? (
            <ItemGridSkeleton count={8} />
          ) : grouped !== null ? (
            grouped.length === 0 ? (
              <div data-testid="items-empty" className="border border-border bg-bone/30 p-10 text-center">
                <p className="font-display text-xl">No items match these filters.</p>
              </div>
            ) : (
              <div className="space-y-10" data-testid="items-grouped">
                {grouped.map((g) => (
                  <div key={g.key} data-testid={`group-${g.key}`}>
                    <div className="flex items-baseline justify-between border-b border-border pb-2 mb-4">
                      <h3 className="font-display text-xl">
                        {g.label}{g.sub ? <span className="text-muted-foreground"> · {g.sub}</span> : ""}
                      </h3>
                      <button className="text-xs uppercase tracking-[0.18em] text-oxblood hover:underline"
                        data-testid={`group-viewall-${g.key}`}
                        onClick={() => {
                          const sp = new URLSearchParams(params);
                          sp.delete("group");
                          if (groupBy === "location" || groupBy === "location_then_type") {
                            const loc = locations.find((l) => l.name === g.label);
                            if (loc) sp.set("location_id", loc._id);
                          }
                          const typeVal = groupBy === "location_type" ? g.label : g.sub;
                          if (typeVal && typeVal !== "Unassigned") sp.set("location_type", typeVal);
                          setParams(sp);
                        }}>
                        View all {g.count}
                      </button>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
                      {g.items.map((it) => (
                        <Link key={it.id} to={`/app/items/${it.id}`} data-testid={`group-item-${it.id}`}
                          className="group block border border-border bg-background">
                          <div className="aspect-[4/5] bg-bone overflow-hidden">
                            {it.primary_photo_url
                              ? <img src={fileUrl(it.primary_photo_url)} alt={it.name} loading="lazy" className="w-full h-full object-contain" />
                              : <div className="w-full h-full grid place-items-center text-[10px] uppercase tracking-[0.2em] text-muted-foreground">No photo</div>}
                          </div>
                          <div className="p-2">
                            <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground truncate">{it.brand || it.category || "—"}</p>
                            <p className="text-sm leading-snug line-clamp-2">{it.name}</p>
                          </div>
                        </Link>
                      ))}
                    </div>
                    {g.count > g.items.length && (
                      <p className="text-xs text-muted-foreground mt-3">Showing {g.items.length} of {g.count} — use "View all" to see the rest.</p>
                    )}
                  </div>
                ))}
              </div>
            )
          ) : !data || data.items.length === 0 ? (
            <div data-testid="items-empty" className="border border-border bg-bone/30 p-10 text-center">
              <p className="font-display text-xl">No items match these filters.</p>
              <p className="text-sm text-muted-foreground mt-2">Try resetting filters or cataloging your first piece.</p>
            </div>
          ) : (
            <>
              {view === "grid" ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="items-grid">
                  {data.items.map((it, i) => (
                    <ItemCard
                      key={it._id} item={it} index={i}
                      selectable={selecting}
                      selected={selected.has(it._id)}
                      onToggleSelect={toggleSelect}
                      locationName={locations.find((l) => l._id === it.current_location_id)?.name}
                    />
                  ))}
                </div>
              ) : (
                <ul className="divide-y divide-border border-y border-border" data-testid="items-list">
                  {data.items.map((it, i) => (
                    <ItemListRow
                      key={it._id} item={it} index={i}
                      selectable={selecting}
                      selected={selected.has(it._id)}
                      onToggleSelect={toggleSelect}
                    />
                  ))}
                </ul>
              )}
              {data.total > data.page_size && (
                <Pagination total={data.total} page={data.page} pageSize={data.page_size}
                  onChange={setPage} />
              )}
            </>
          )}
        </section>
      </div>

      <Sheet open={mobileFilterOpen} onOpenChange={setMobileFilterOpen}>
        <SheetContent side="bottom" data-testid="items-mobile-filter-sheet"
          className="bg-background rounded-none max-h-[85vh] overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="font-display text-2xl text-left">Filter items</SheetTitle>
          </SheetHeader>
          <div className="mt-4 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input data-testid="items-search-mobile" placeholder="Search name, brand, tags…"
              value={search} onChange={(e: any) => setSearch(e.target.value)} className="pl-9" />
          </div>
          <div className="mt-6 space-y-6">{filterPanel}</div>
          <Button onClick={() => setMobileFilterOpen(false)}
            data-testid="items-mobile-filter-apply"
            className="mt-6 w-full bg-oxblood hover:bg-oxblood-deep text-ivory rounded-none h-11">
            Show {data?.total ?? 0} item{data?.total === 1 ? "" : "s"}
          </Button>
        </SheetContent>
      </Sheet>

      <BulkActionsBar
        selected={Array.from(selected)}
        locations={locations}
        onClear={exitSelectMode}
        onAfterAction={reload}
      />

      <HelpOverlay open={helpOpen} onClose={() => setHelpOpen(false)} />
    </main>
  );
}

function ItemListRow({ item, index, selectable, selected, onToggleSelect }: {
  item: import("@/lib/catalog-api").Item;
  index: number;
  selectable: boolean;
  selected: boolean;
  onToggleSelect: (id: string) => void;
}) {
  const primary = item.photos.find((p) => p.is_primary) || item.photos[0];
  const photoUrl = fileUrl(primary?.url);
  const inner = (
    <>
      <div className="w-12 h-14 bg-bone overflow-hidden shrink-0">
        {photoUrl ? (
          <img src={photoUrl} alt="" className="w-full h-full object-cover" loading="lazy" />
        ) : null}
      </div>
      <div className="min-w-0 flex-1 grid grid-cols-[1fr_auto] gap-3 items-center">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground truncate">
            {item.brand || item.category}
          </p>
          <p className="font-display text-sm leading-snug truncate mt-0.5">{item.name}</p>
          <p className="text-[11px] text-muted-foreground mt-0.5 truncate">
            {item.size || "—"} · {item.condition} · <span className="font-mono">{item.internal_code}</span>
          </p>
        </div>
        <span className="text-[11px] text-muted-foreground tabular-nums">
          {formatMoney(item.insured_value || item.retail_value || undefined)}
        </span>
      </div>
    </>
  );

  if (selectable) {
    return (
      <li>
        <button
          type="button"
          data-testid={`item-row-${index}`}
          onClick={() => onToggleSelect(item._id)}
          className={`w-full text-left flex items-center gap-3 px-3 py-3 transition-colors ${
            selected ? "bg-oxblood/5" : "hover:bg-bone/50"
          }`}
        >
          <span className={`w-5 h-5 grid place-items-center border shrink-0 ${
            selected ? "bg-oxblood border-oxblood text-ivory" : "border-border text-transparent"
          }`}>
            ✓
          </span>
          {inner}
        </button>
      </li>
    );
  }
  return (
    <li>
      <Link
        to={`/app/items/${item._id}`}
        data-testid={`item-row-${index}`}
        className="flex items-center gap-3 px-3 py-3 hover:bg-bone/50 transition-colors"
      >
        {inner}
      </Link>
    </li>
  );
}

function FilterPanel({
  data, params, clients, locations, locationTypes, categories, brands,
  onToggleMulti, onSetSingle, onReset, activeFilterCount,
}: {
  data: ItemListResponse | null;
  params: URLSearchParams;
  clients: ClientRecord[];
  locations: LocationRecord[];
  locationTypes: string[];
  categories: string[];
  brands: string[];
  onToggleMulti: (k: "brand" | "category" | "condition" | "season", v: string) => void;
  onSetSingle: (k: string, v: string | null) => void;
  onReset: () => void;
  activeFilterCount: number;
}) {
  const opts = useItemOptions();
  return (
    <div className="space-y-6">
      <SelectFacet title="Category" facets={data?.facets.category} options={categories} sortAlpha
                  value={params.get("category") || ""} onChange={(v) => onSetSingle("category", v || null)} testid="facet-category" />
      <SelectFacet title="Brand" facets={data?.facets.brand} options={brands} sortAlpha
                  value={params.get("brand") || ""} onChange={(v) => onSetSingle("brand", v || null)} testid="facet-brand" />
      <SelectFacet title="Color family" facets={data?.facets.color_family} options={opts?.color_families.map((f) => f.family)}
                  value={params.get("color_family") || ""} onChange={(v) => onSetSingle("color_family", v || null)} testid="facet-color-family" />
      <SelectFacet title="Material" facets={data?.facets.material} options={opts?.materials} sortAlpha
                  value={params.get("material") || ""} onChange={(v) => onSetSingle("material", v || null)} testid="facet-material" />
      <SelectFacet title="Size" facets={data?.facets.size}
                  options={opts ? [...opts.clothing_sizes.flatMap((g) => g.options), ...Object.values(opts.shoe_size_systems).flat()] : undefined}
                  value={params.get("size") || ""} onChange={(v) => onSetSingle("size", v || null)} testid="facet-size" />
      <SelectFacet title="Shoe type" facets={data?.facets.shoe_type} options={opts?.shoe_types}
                  value={params.get("shoe_type") || ""} onChange={(v) => onSetSingle("shoe_type", v || null)} testid="facet-shoe-type" />
      <SelectFacet title="Condition" facets={data?.facets.condition} options={CONDITIONS}
                  value={params.get("condition") || ""} onChange={(v) => onSetSingle("condition", v || null)} testid="facet-condition" />
      <SelectFacet title="Season" facets={data?.facets.season} options={SEASONS}
                  value={params.get("season") || ""} onChange={(v) => onSetSingle("season", v || null)} testid="facet-season" />

      <div className="space-y-2">
        <p className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">Location</p>
        <select data-testid="filter-location" className="w-full h-9 border border-border bg-background px-2 text-sm"
          value={params.get("location_id") || ""}
          onChange={(e) => onSetSingle("location_id", e.target.value || null)}>
          <option value="">All locations</option>
          {locations.map((l) => <option key={l._id} value={l._id}>{l.name}</option>)}
        </select>
      </div>

      <SelectFacet title="Location type" facets={data?.facets.location_type} options={locationTypes} sortAlpha
                  value={params.get("location_type") || ""} onChange={(v) => onSetSingle("location_type", v || null)} testid="facet-location-type" />

      <label className="flex items-center gap-2 text-sm text-muted-foreground">
        <input type="checkbox" data-testid="filter-archived"
          checked={params.get("archived") === "1"}
          onChange={(e) => onSetSingle("archived", e.target.checked ? "1" : null)} />
        Show archived
      </label>

      {activeFilterCount > 0 && (
        <button type="button" onClick={onReset} data-testid="filters-reset"
          className="text-xs text-oxblood hover:underline underline-offset-4">
          Reset filters ({activeFilterCount})
        </button>
      )}
    </div>
  );
}

function SelectFacet({ title, facets, options, value, onChange, testid, sortAlpha }: {
  title: string; facets?: FacetBucket[]; options?: readonly string[];
  value: string; onChange: (v: string) => void; testid: string; sortAlpha?: boolean;
}) {
  const counts = new Map<string, number>();
  (facets || []).forEach((f) => counts.set(f.value.toLowerCase(), (counts.get(f.value.toLowerCase()) || 0) + f.count));
  const seen = new Set<string>();
  let display: { value: string; count?: number }[] = [];
  [...(options || []), ...(facets || []).map((f) => f.value), ...(value ? [value] : [])].forEach((v) => {
    const k = v.toLowerCase();
    if (seen.has(k)) return;
    seen.add(k);
    display.push({ value: v, count: counts.get(k) ?? 0 });
  });
  if (sortAlpha) display = [...display].sort((a, b) => a.value.toLowerCase().localeCompare(b.value.toLowerCase()));
  if (display.length === 0) return null;
  return (
    <div className="space-y-2" data-testid={testid}>
      <p className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">{title}</p>
      <select data-testid={`${testid}-select`} className="w-full h-9 border border-border bg-background px-2 text-sm"
        value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">All {(title.endsWith("y") ? title.slice(0, -1) + "ies" : title + "s").toLowerCase()}</option>
        {display.map((d) => (
          <option key={d.value} value={d.value}>{d.value}{d.count !== undefined ? ` (${d.count})` : ""}</option>
        ))}
      </select>
    </div>
  );
}

function Pagination({ total, page, pageSize, onChange }: {
  total: number; page: number; pageSize: number; onChange: (p: number) => void;
}) {
  const pages = Math.ceil(total / pageSize);
  if (pages <= 1) return null;
  // Windowed page numbers around the current page (max 5) with first/last.
  const window: number[] = [];
  const start = Math.max(1, Math.min(page - 2, pages - 4));
  const end = Math.min(pages, start + 4);
  for (let p = start; p <= end; p++) window.push(p);
  const btn = "min-w-9 h-9 px-2 border border-border text-sm transition-colors disabled:opacity-30";
  return (
    <div className="mt-8 flex flex-wrap items-center justify-center gap-1.5 text-sm" data-testid="items-pagination">
      <button type="button" disabled={page === 1} onClick={() => onChange(page - 1)}
        data-testid="pagination-prev" className={`${btn} hover:text-oxblood`}>Prev</button>
      {start > 1 && (
        <>
          <button type="button" onClick={() => onChange(1)} className={`${btn} hover:text-oxblood`}>1</button>
          {start > 2 && <span className="px-1 text-muted-foreground">…</span>}
        </>
      )}
      {window.map((p) => (
        <button key={p} type="button" onClick={() => onChange(p)}
          data-testid={`pagination-page-${p}`}
          aria-current={p === page}
          className={`${btn} ${p === page ? "bg-foreground text-ivory border-foreground" : "hover:text-oxblood"}`}>
          {p}
        </button>
      ))}
      {end < pages && (
        <>
          {end < pages - 1 && <span className="px-1 text-muted-foreground">…</span>}
          <button type="button" onClick={() => onChange(pages)} className={`${btn} hover:text-oxblood`}>{pages}</button>
        </>
      )}
      <button type="button" disabled={page >= pages} onClick={() => onChange(page + 1)}
        data-testid="pagination-next" className={`${btn} hover:text-oxblood`}>Next</button>
    </div>
  );
}

function ClientPortalDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [links, setLinks] = useState<SharedLink[]>([]);
  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState("");
  const [showValues, setShowValues] = useState(false);
  const [creating, setCreating] = useState(false);

  const portals = links.filter((l) => l.kind === "workspace" && !l.revoked);

  const load = useCallback(() => {
    setLoading(true);
    sharesApi.list().then(setLinks).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { if (open) { load(); setPassword(""); setShowValues(false); } }, [open, load]);

  async function create() {
    setCreating(true);
    try {
      await sharesApi.create({ kind: "workspace", title: "Client Portal", expiry: "never", password: password.trim() || undefined, show_values: showValues });
      toast.success("Client portal link created.");
      setPassword(""); load();
    } catch (e: any) { toast.error(e?.message || "Could not create link."); }
    finally { setCreating(false); }
  }
  async function revoke(id: string) {
    try { await sharesApi.revoke(id); toast.success("Link revoked."); load(); }
    catch (e: any) { toast.error(e?.message || "Could not revoke."); }
  }
  function linkFor(token: string) { return `${window.location.origin}/portal/${token}`; }

  return (
    <Dialog open={open} onOpenChange={(v: boolean) => !v && onClose()}>
      <DialogContent data-testid="client-portal-dialog" className="rounded-none max-w-lg">
        <DialogHeader><DialogTitle className="font-display text-2xl">Client portal</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Create a private link your client can open (no login) to browse the whole collection and see where each piece lives.</p>

        <div className="border border-border p-4 space-y-3">
          <p className="text-[11px] uppercase tracking-[0.18em] text-oxblood">Create a link</p>
          <div>
            <label className="text-xs text-muted-foreground">Password (recommended)</label>
            <input data-testid="portal-create-password" type="text" value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="Optional but recommended" className="h-10 w-full border border-border bg-background px-3 text-sm mt-1" />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" data-testid="portal-show-values" checked={showValues} onChange={(e) => setShowValues(e.target.checked)} />
            Show item values ($) to the client
          </label>
          <Button onClick={create} disabled={creating} data-testid="portal-create-btn"
            className="bg-oxblood hover:bg-oxblood-deep text-ivory rounded-none">
            {creating ? "Creating…" : "Create portal link"}
          </Button>
        </div>

        <div className="space-y-2 max-h-64 overflow-auto" data-testid="portal-links">
          {loading && <p className="text-sm text-muted-foreground">Loading…</p>}
          {!loading && portals.length === 0 && <p className="text-sm text-muted-foreground">No active portal links yet.</p>}
          {portals.map((l) => (
            <div key={l._id} className="border border-border p-3 text-sm" data-testid={`portal-link-${l._id}`}>
              <div className="flex items-center justify-between gap-2">
                <code className="text-xs break-all">{linkFor(l.token)}</code>
                <div className="flex gap-2 shrink-0">
                  <button className="text-xs uppercase tracking-[0.16em] text-oxblood hover:underline"
                    data-testid={`portal-copy-${l._id}`}
                    onClick={() => { navigator.clipboard.writeText(linkFor(l.token)); toast.success("Link copied."); }}>Copy</button>
                  <button className="text-xs uppercase tracking-[0.16em] text-muted-foreground hover:text-oxblood"
                    data-testid={`portal-revoke-${l._id}`}
                    onClick={() => revoke(l._id)}>Revoke</button>
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">
                {l.has_password ? "🔒 Password-protected" : "Open (no password)"} · {l.show_values ? "values shown" : "values hidden"} · {l.view_count} views
              </p>
            </div>
          ))}
        </div>

        <DialogFooter><Button variant="outline" onClick={onClose} className="rounded-none">Done</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

